import { useEffect, useMemo, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  EditButton,
  Pagination,
  SelectInput,
  SearchInput,
  Create,
  Edit,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  NumberInput,
  BooleanInput,
  RadioButtonGroupInput,
  DateTimeInput,
  FormDataConsumer,
  TopToolbar,
  ListButton,
  useInput,
  useNotify,
  useRecordContext,
  useRefresh,
  required,
  minValue,
  maxValue,
  maxLength,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { Bolt, PauseCircle, PlayCircle } from "@mui/icons-material";
import { promotionProvider } from "../../dataProviders";
import {
  promotionStatus,
  type PromotionFormValues,
  type PromotionStatus,
} from "../../dataProviders/promotionProvider";
import { API_PUBLIC_BASE_URL } from "../../config";
import { handleApiError } from "../../utils/errorHandler";
import type { Promotion, PromotionCity } from "../../types/api";

// ---------------------------------------------------------------------------
// Limits — mirror the backend (promotion-admin.service.ts). The backend
// re-validates everything; these only give the admin fast feedback.
// ---------------------------------------------------------------------------

const MAX_PROMOTION_DAYS = 62;
const MAX_LIGHTNING_HOURS = 72;
const MAX_PROMOTION_CITIES = 200;
const HOUR_MS = 3_600_000;
const DEFAULT_COVERAGE: Record<string, number> = {
  "3": 1,
  "4": 1,
  "5": 1,
  default: 0.6,
};

const STAR_KEYS: Array<{ key: string; label: string }> = [
  { key: "5", label: "5★" },
  { key: "4", label: "4★" },
  { key: "3", label: "3★" },
  { key: "2", label: "2★" },
  { key: "1", label: "1★" },
  { key: "0", label: "Unrated" },
  { key: "default", label: "Any other" },
];

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const STATUS_STYLE: Record<
  PromotionStatus,
  { label: string; color: "success" | "info" | "default" | "warning" }
> = {
  live: { label: "Live", color: "success" },
  scheduled: { label: "Scheduled", color: "info" },
  ended: { label: "Ended", color: "default" },
  inactive: { label: "Off", color: "warning" },
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const formatDuration = (ms: number) => {
  if (ms <= 0) return "0h";
  const hours = ms / HOUR_MS;
  if (hours < 48) return `${Math.round(hours * 10) / 10}h`;
  return `${Math.round(hours / 24)} days`;
};

const formatCities = (cities: PromotionCity[] | undefined, max = 3) => {
  if (!cities?.length) return "All cities";
  const names = cities.map((c) => c.name);
  return names.length > max
    ? `${names.slice(0, max).join(", ")} +${names.length - max}`
    : names.join(", ");
};

const formatDiscount = (r: Promotion) =>
  r.discountType === "fixed"
    ? `€${Number(r.fixedDiscountAmount)} off per ${r.fixedDiscountPer === "night" ? "night" : "stay"}`
    : `${r.minDiscountPercent}–${r.maxDiscountPercent}%`;

const formatCoverage = (coverage: Record<string, number> | undefined) => {
  if (!coverage) return "—";
  return STAR_KEYS.filter(({ key }) => coverage[key] != null)
    .map(
      ({ key, label }) =>
        `${label} ${Math.round(Number(coverage[key]) * 100)}%`,
    )
    .join(" · ");
};

const PromotionStatusChip = () => {
  const record = useRecordContext<Promotion>();
  if (!record) return null;
  const style = STATUS_STYLE[promotionStatus(record)];
  return (
    <Chip
      size="small"
      label={style.label}
      color={style.color}
      variant={style.color === "default" ? "outlined" : "filled"}
    />
  );
};

const PromotionTypeChip = () => {
  const record = useRecordContext<Promotion>();
  if (!record) return null;
  return record.isLightning ? (
    <Chip
      size="small"
      icon={<Bolt />}
      label="Lightning"
      sx={{
        bgcolor: "#fbbf24",
        color: "#451a03",
        "& .MuiChip-icon": { color: "#451a03" },
      }}
    />
  ) : (
    <Chip size="small" label="Regular" variant="outlined" />
  );
};

/** Switch a promotion on/off in place (the API has no delete, by design). */
const ToggleActiveButton = () => {
  const record = useRecordContext<Promotion>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (!record) return null;
  const next = !record.isActive;
  return (
    <Button
      size="small"
      disabled={busy}
      startIcon={next ? <PlayCircle /> : <PauseCircle />}
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
          await promotionProvider.setActive(record.id, next);
          notify(next ? "Promotion switched on" : "Promotion switched off", {
            type: "success",
          });
          refresh();
        } catch (error) {
          notify(handleApiError(error).message, { type: "error" });
        } finally {
          setBusy(false);
        }
      }}
    >
      {next ? "Switch on" : "Switch off"}
    </Button>
  );
};

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

const promotionFilters = [
  <SearchInput key="q" source="q" placeholder="Name or city" alwaysOn />,
  <SelectInput
    key="status"
    source="status"
    alwaysOn
    choices={[
      { id: "live", name: "Live" },
      { id: "scheduled", name: "Scheduled" },
      { id: "ended", name: "Ended" },
      { id: "inactive", name: "Off" },
    ]}
  />,
  <SelectInput
    key="type"
    source="type"
    alwaysOn
    choices={[
      { id: "lightning", name: "Lightning" },
      { id: "regular", name: "Regular" },
    ]}
  />,
];

export const PromotionList = () => (
  <List
    title="Promotions"
    filters={promotionFilters}
    perPage={25}
    pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="name" />
      <FunctionField label="Status" render={() => <PromotionStatusChip />} />
      <FunctionField label="Type" render={() => <PromotionTypeChip />} />
      <FunctionField
        label="Window"
        render={(r: Promotion) => (
          <Box>
            <Typography variant="body2">
              {formatDateTime(r.startsAt)} → {formatDateTime(r.endsAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDuration(
                new Date(r.endsAt).getTime() - new Date(r.startsAt).getTime(),
              )}
            </Typography>
          </Box>
        )}
      />
      <FunctionField label="Discount" render={formatDiscount} />
      <FunctionField
        label="List markup"
        render={(r: Promotion) =>
          r.listMarkupPercent == null ? "Normal" : `${r.listMarkupPercent}%`
        }
      />
      <FunctionField
        label="Margin band"
        render={(r: Promotion) =>
          `${r.minMarginPercent}–${r.maxMarginPercent}%`
        }
      />
      <FunctionField
        label="Cities"
        render={(r: Promotion) => formatCities(r.regions)}
      />
      <ToggleActiveButton />
      <EditButton />
    </Datagrid>
  </List>
);

// ---------------------------------------------------------------------------
// Form pieces
// ---------------------------------------------------------------------------

interface RegionSearchResult {
  regionId?: string | number;
  id?: string | number;
  name: string;
  countryCode?: string;
  countryName?: string;
  type?: string;
}

/**
 * Multi-select city picker backed by the public GET /regions/search. Stores
 * [{ regionId, name, countryCode }] in `cities`; the backend re-checks every
 * entry and rejects regionIds that are shared by several places without a name.
 */
const CityPickerInput = () => {
  const { field, fieldState } = useInput({
    source: "cities",
    defaultValue: [],
  });
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<PromotionCity[]>([]);
  const [loading, setLoading] = useState(false);
  const value: PromotionCity[] = Array.isArray(field.value) ? field.value : [];

  useEffect(() => {
    if (query.trim().length < 2) {
      setOptions([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_PUBLIC_BASE_URL}/regions/search?name=${encodeURIComponent(query.trim())}`,
          {
            signal: controller.signal,
          },
        );
        const body = await res.json();
        const rows: RegionSearchResult[] = Array.isArray(body)
          ? body
          : (body?.data ?? []);
        setOptions(
          rows
            .filter((r) => (r.regionId ?? r.id) != null && r.name)
            .map((r) => ({
              regionId: String(r.regionId ?? r.id),
              name: r.name,
              countryCode: r.countryCode ?? null,
              countryName: r.countryName,
              type: r.type,
            })) as PromotionCity[],
        );
      } catch (err) {
        if ((err as Error).name !== "AbortError") setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query]);

  const keyOf = (c: PromotionCity) => `${c.regionId}|${c.name.toLowerCase()}`;
  const describe = (
    c: PromotionCity & { countryName?: string; type?: string },
  ) =>
    [c.name, c.countryName ?? c.countryCode, c.type].filter(Boolean).join(", ");

  return (
    <Autocomplete
      multiple
      fullWidth
      options={options}
      value={value}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => keyOf(a) === keyOf(b)}
      getOptionLabel={(c) => describe(c)}
      onInputChange={(_e, v, reason) => reason === "input" && setQuery(v)}
      onChange={(_e, next) =>
        field.onChange(next.slice(0, MAX_PROMOTION_CITIES))
      }
      renderOption={(props, c) => (
        <li {...props} key={keyOf(c)}>
          <Box>
            <Typography variant="body2">{describe(c)}</Typography>
            <Typography variant="caption" color="text.secondary">
              regionId {c.regionId}
            </Typography>
          </Box>
        </li>
      )}
      renderValue={(selected, getItemProps) =>
        selected.map((c, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              size="small"
              label={`${c.name}${c.countryCode ? ` (${c.countryCode})` : ""}`}
              {...itemProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label="Cities"
          placeholder={
            value.length
              ? "Add another city"
              : "All cities — type to limit to specific cities"
          }
          error={!!fieldState.error}
          helperText={
            fieldState.error?.message ??
            "Leave empty for every city. Hotels match on their own city, so add suburbs separately if you want them included. A city promotion beats a global one."
          }
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

/** Share of hotels included per star rating, edited in percent, stored 0-1. */
const CoverageInput = () => {
  const { field } = useInput({
    source: "coverageByStars",
    defaultValue: DEFAULT_COVERAGE,
  });
  const coverage: Record<string, number> = field.value ?? {};
  const set = (key: string, raw: string) => {
    const next = { ...coverage };
    if (raw === "") delete next[key];
    else next[key] = Math.min(100, Math.max(0, Number(raw))) / 100;
    field.onChange(next);
  };
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Hotels included by star rating
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {STAR_KEYS.map(({ key, label }) => (
          <MuiTextField
            key={key}
            label={label}
            type="number"
            size="small"
            sx={{ width: 110 }}
            value={
              coverage[key] == null
                ? ""
                : Math.round(Number(coverage[key]) * 100)
            }
            onChange={(e) => set(key, e.target.value)}
            slotProps={{
              htmlInput: { min: 0, max: 100, step: 5 },
              input: { endAdornment: <span>%</span> },
            }}
          />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Which hotels are picked is random but fixed per hotel, so every page and
        checkout agree. Blank uses &ldquo;Any other&rdquo;.
      </Typography>
    </Box>
  );
};

/** Quick lengths for lightning deals, counted from the start time. */
const LightningDurationButtons = () => {
  const { getValues, setValue } = useFormContext();
  const apply = (hours: number) => {
    const start = getValues("startsAt")
      ? new Date(getValues("startsAt"))
      : new Date();
    if (!getValues("startsAt"))
      setValue("startsAt", start, { shouldDirty: true });
    setValue("endsAt", new Date(start.getTime() + hours * HOUR_MS), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Quick length:
      </Typography>
      {[3, 6, 12, 24, 48].map((h) => (
        <Button
          key={h}
          size="small"
          variant="outlined"
          onClick={() => apply(h)}
        >
          {h}h
        </Button>
      ))}
    </Stack>
  );
};

const clampMargin = (m: number, lo: number, hi: number) =>
  Math.min(Math.max(m, lo), hi);

/** What customers will pay, in margin terms, given the form's numbers. */
const MarginPreview = ({ data }: { data: PromotionFormValues }) => {
  if (data.discountType === "fixed") {
    const amount = Number(data.fixedDiscountAmount);
    if (!(amount > 0)) return null;
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        €{amount} off every room, per{" "}
        {data.fixedDiscountPer === "night" ? "night" : "stay"}, converted to the
        visitor&rsquo;s currency. Promoted margins still stay between{" "}
        {Number(data.minMarginPercent)}% and {Number(data.maxMarginPercent)}%:
        on cheap rooms customers may save less than €{amount}, and if €{amount}{" "}
        would leave a margin above the ceiling the discount is deepened to meet
        it.
      </Alert>
    );
  }
  const minD = Number(data.minDiscountPercent);
  const maxD = Number(data.maxDiscountPercent);
  const floor = Number(data.minMarginPercent);
  const ceiling = Number(data.maxMarginPercent);
  const list =
    data.listMarkupPercent === "" || data.listMarkupPercent == null
      ? null
      : Number(data.listMarkupPercent);
  if (![minD, maxD, floor, ceiling].every(Number.isFinite)) return null;

  if (list == null) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Discount is taken off each provider&rsquo;s normal markup. Promoted
        margins stay between {floor}% and {ceiling}%, and a promotion never
        raises a price.
      </Alert>
    );
  }
  const marginAt = (d: number) => ((1 + list / 100) * (1 - d / 100) - 1) * 100;
  const lo = clampMargin(marginAt(maxD), floor, ceiling);
  const hi = clampMargin(marginAt(minD), floor, ceiling);
  const effectiveMax = (1 - (1 + floor / 100) / (1 + list / 100)) * 100;
  return (
    <Alert severity={effectiveMax < maxD ? "warning" : "info"} sx={{ mb: 2 }}>
      {minD}–{maxD}% off a {list}% list markup puts promoted margins at{" "}
      {lo.toFixed(1)}–{hi.toFixed(1)}%.
      {effectiveMax < maxD &&
        ` Your ${floor}% margin floor caps the discount at ${Math.max(0, effectiveMax).toFixed(1)}%.`}{" "}
      Promotions never charge more than a customer&rsquo;s normal price, so
      against a lower normal markup the saving is smaller.
    </Alert>
  );
};

const validatePromotion = (values: PromotionFormValues) => {
  const errors: Record<string, string> = {};
  const start = values.startsAt ? new Date(values.startsAt).getTime() : NaN;
  const end = values.endsAt ? new Date(values.endsAt).getTime() : NaN;
  if (Number.isFinite(start) && Number.isFinite(end)) {
    if (end <= start) errors.endsAt = "Must be after the start";
    else if (values.isLightning && end - start > MAX_LIGHTNING_HOURS * HOUR_MS)
      errors.endsAt = `A lightning deal can run at most ${MAX_LIGHTNING_HOURS} hours`;
    else if (end - start > MAX_PROMOTION_DAYS * 24 * HOUR_MS)
      errors.endsAt = `A promotion can run at most ${MAX_PROMOTION_DAYS} days`;
  }
  if (values.discountType === "fixed") {
    const amount = Number(values.fixedDiscountAmount);
    if (!(amount > 0) || amount > 10000)
      errors.fixedDiscountAmount = "Enter an amount between 0 and 10,000";
  } else if (
    Number(values.minDiscountPercent) > Number(values.maxDiscountPercent)
  )
    errors.maxDiscountPercent = "Must be at least the minimum";
  if (Number(values.minMarginPercent) > Number(values.maxMarginPercent))
    errors.maxMarginPercent = "Must be at least the minimum";
  if (
    Array.isArray(values.cities) &&
    values.cities.length > MAX_PROMOTION_CITIES
  )
    errors.cities = `At most ${MAX_PROMOTION_CITIES} cities`;
  return errors;
};

const percentRange = (max: number) => [required(), minValue(0), maxValue(max)];

const PromotionForm = () => (
  <SimpleForm validate={validatePromotion} sx={{ maxWidth: 820 }}>
    <TextInput source="name" validate={required()} fullWidth />

    <BooleanInput
      source="isLightning"
      label="Lightning deal"
      helperText={`A flash sale of up to ${MAX_LIGHTNING_HOURS} hours. It beats any regular promotion for the same hotel, and the website shows a live countdown.`}
    />

    <Stack
      direction={{ xs: "column", sm: "row" }}
      gap={2}
      sx={{ width: "100%" }}
    >
      <DateTimeInput
        source="startsAt"
        label="Starts"
        validate={required()}
        fullWidth
      />
      <DateTimeInput
        source="endsAt"
        label="Ends"
        validate={required()}
        fullWidth
      />
    </Stack>
    <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
      Times are in your browser&rsquo;s timezone. Regular promotions show a
      countdown on the website in their last 24 hours.
    </Typography>
    <FormDataConsumer>
      {({ formData }) =>
        formData.isLightning ? <LightningDurationButtons /> : null
      }
    </FormDataConsumer>

    <Typography variant="h6" sx={{ mt: 2 }}>
      Pricing
    </Typography>
    <NumberInput
      source="listMarkupPercent"
      label="List markup (%)"
      validate={[minValue(0), maxValue(100)]}
      helperText="Markup the discount range is taken from (e.g. 15). Leave blank to discount from each provider's normal markup."
      fullWidth
    />
    <RadioButtonGroupInput
      source="discountType"
      label="Discount type"
      choices={[
        { id: "percentage", name: "Percentage range" },
        { id: "fixed", name: "Fixed amount (€)" },
      ]}
    />
    <FormDataConsumer>
      {({ formData }) =>
        formData.discountType === "fixed" ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ width: "100%" }}
          >
            <NumberInput
              source="fixedDiscountAmount"
              label="Amount off (€)"
              validate={[required(), minValue(0.01), maxValue(10000)]}
              helperText="Converted to the visitor's currency at the day's rate"
              fullWidth
            />
            <SelectInput
              source="fixedDiscountPer"
              label="Per"
              validate={required()}
              choices={[
                { id: "stay", name: "Stay (whole booking)" },
                { id: "night", name: "Night" },
              ]}
              fullWidth
            />
          </Stack>
        ) : (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={2}
              sx={{ width: "100%" }}
            >
              <NumberInput
                source="minDiscountPercent"
                label="Discount from (%)"
                validate={percentRange(90)}
                fullWidth
              />
              <NumberInput
                source="maxDiscountPercent"
                label="Discount to (%)"
                validate={percentRange(90)}
                fullWidth
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: -1, mb: 1, display: "block" }}
            >
              Each hotel gets a random discount in this range, fixed for the
              whole promotion.
            </Typography>
          </>
        )
      }
    </FormDataConsumer>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      gap={2}
      sx={{ width: "100%" }}
    >
      <NumberInput
        source="minMarginPercent"
        label="Margin floor (%)"
        validate={percentRange(100)}
        fullWidth
      />
      <NumberInput
        source="maxMarginPercent"
        label="Margin ceiling (%)"
        validate={percentRange(100)}
        fullWidth
      />
    </Stack>
    <FormDataConsumer>
      {({ formData }) => <MarginPreview data={formData} />}
    </FormDataConsumer>

    <Typography variant="h6" sx={{ mt: 1 }}>
      Where
    </Typography>
    <CityPickerInput />
    <Box sx={{ mt: 2, width: "100%" }}>
      <CoverageInput />
    </Box>

    <Typography variant="h6" sx={{ mt: 2 }}>
      Search results popup
    </Typography>
    <BooleanInput
      source="popupEnabled"
      label="Show a popup when customers search a covered city"
      helperText="Shown once per promotion per browser session."
    />
    <FormDataConsumer>
      {({ formData }) =>
        formData.popupEnabled === false ? null : (
          <>
            <TextInput
              source="popupTitle"
              label="Popup title"
              validate={maxLength(80)}
              helperText={
                formData.isLightning
                  ? 'Optional. Default: "Lightning deal in <city>".'
                  : 'Optional. Default: "<city> deals".'
              }
              fullWidth
            />
            <TextInput
              source="popupMessage"
              label="Popup message"
              validate={maxLength(280)}
              multiline
              minRows={2}
              helperText={
                formData.discountType === "fixed"
                  ? 'Optional. Default: "Up to €X off your stay at selected hotels." The website adds a countdown and a "Save up to" figure from the actual results.'
                  : 'Optional. Default: "Lower prices at selected hotels for a limited time." The website adds a countdown and a "Save up to" figure from the actual results.'
              }
              fullWidth
            />
          </>
        )
      }
    </FormDataConsumer>

    <BooleanInput source="isActive" label="Active" sx={{ mt: 2 }} />
    <Alert severity="info" sx={{ width: "100%" }}>
      The website shows a crossed-out &ldquo;was&rdquo; price only when it is
      the lowest price customers actually paid in the 30 days before the
      promotion started; otherwise customers get the lower price without one.
    </Alert>
  </SimpleForm>
);

// ---------------------------------------------------------------------------
// Create / Edit / Show
// ---------------------------------------------------------------------------

const nextFullHour = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
};

export const PromotionCreate = () => {
  const defaults = useMemo(() => {
    const start = nextFullHour();
    return {
      isLightning: false,
      isActive: true,
      startsAt: start,
      endsAt: new Date(start.getTime() + 7 * 24 * HOUR_MS),
      discountType: "percentage",
      minDiscountPercent: 8,
      maxDiscountPercent: 12,
      fixedDiscountAmount: 20,
      fixedDiscountPer: "stay",
      popupEnabled: true,
      minMarginPercent: 3.5,
      maxMarginPercent: 8,
      listMarkupPercent: null,
      coverageByStars: DEFAULT_COVERAGE,
      cities: [],
    };
  }, []);
  return (
    <Create title="New promotion" redirect="show" record={defaults}>
      <PromotionForm />
    </Create>
  );
};

const EditActions = () => (
  <TopToolbar>
    <ListButton />
    <ToggleActiveButton />
  </TopToolbar>
);

export const PromotionEdit = () => (
  <Edit
    title="Edit promotion"
    mutationMode="pessimistic"
    redirect="show"
    actions={<EditActions />}
  >
    <PromotionForm />
  </Edit>
);

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <ToggleActiveButton />
    <EditButton />
  </TopToolbar>
);

export const PromotionShow = () => (
  <Show title="Promotion" actions={<ShowActions />}>
    <SimpleShowLayout>
      <TextField source="name" />
      <FunctionField label="Status" render={() => <PromotionStatusChip />} />
      <FunctionField label="Type" render={() => <PromotionTypeChip />} />
      <FunctionField
        label="Starts"
        render={(r: Promotion) => formatDateTime(r.startsAt)}
      />
      <FunctionField
        label="Ends"
        render={(r: Promotion) =>
          `${formatDateTime(r.endsAt)} (${formatDuration(new Date(r.endsAt).getTime() - new Date(r.startsAt).getTime())})`
        }
      />
      <FunctionField
        label="List markup"
        render={(r: Promotion) =>
          r.listMarkupPercent == null
            ? "Each provider's normal markup"
            : `${r.listMarkupPercent}%`
        }
      />
      <FunctionField label="Discount" render={formatDiscount} />
      <FunctionField
        label="Margin band"
        render={(r: Promotion) =>
          `${r.minMarginPercent}–${r.maxMarginPercent}%`
        }
      />
      <FunctionField
        label="Cities"
        render={(r: Promotion) => formatCities(r.regions, 50)}
      />
      <FunctionField
        label="Hotels included"
        render={(r: Promotion) => formatCoverage(r.coverageByStars)}
      />
      <FunctionField
        label="Created"
        render={(r: Promotion) => formatDateTime(r.createdAt)}
      />
      <FunctionField
        label="Updated"
        render={(r: Promotion) => formatDateTime(r.updatedAt)}
      />
    </SimpleShowLayout>
  </Show>
);
